import { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Search, Copy, Download, Trash2, FileText, FileType, Star, Loader2, ArrowLeft, Code2, Eye, Send } from "lucide-react";
import { VaultOutputRenderer } from "@/components/VaultOutputRenderer";

const CATEGORIES = ["all", "reels", "carousels", "captions", "hooks", "emails", "ads", "blogs", "promos", "funnels", "outreach", "plans", "replies"];

export function ContentVault() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [q, setQ] = useState("");
  const [banked, setBanked] = useState(false);

  const runSearch = useCallback(async (searchTerm) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category !== "all") params.set("category", category);
      if (searchTerm) params.set("q", searchTerm);
      if (banked) params.set("banked", "true");
      const { data } = await api.get(`/vault?${params.toString()}`);
      setItems(data.items || []);
    } finally { setLoading(false); }
  }, [category, banked]);

  // Auto-reload only when filters (category/banked) change; search is manual via the form.
  useEffect(() => { runSearch(""); }, [runSearch]);

  const onSearch = (e) => { e.preventDefault(); runSearch(q); };

  const del = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/vault/${id}`);
    setItems((arr) => arr.filter((x) => x.id !== id));
    toast.success("Deleted");
  };

  const duplicate = async (id) => {
    const { data } = await api.post(`/vault/${id}/duplicate`);
    setItems((arr) => [data.item, ...arr]);
    toast.success("Duplicated");
  };

  const bank = async (item) => {
    const { data } = await api.put(`/vault/${item.id}`, { is_banked: !item.is_banked });
    setItems((arr) => arr.map((x) => x.id === item.id ? data.item : x));
  };

  return (
    <div data-testid="vault-page" className="px-5 sm:px-6 lg:px-10 py-8 sm:py-10 max-w-6xl">
      <div className="dms-label mb-3">Your library</div>
      <h1 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold text-[#0A2342]">Content Vault</h1>
      <p className="mt-2 text-[#475569] text-[14.5px]">Every output you&apos;ve generated. Auto-saved. Searchable. Yours to edit, duplicate, download.</p>

      <form onSubmit={onSearch} className="mt-6 sm:mt-8 dms-card p-3 sm:p-4 flex flex-col sm:flex-row gap-2 sm:items-center">
        <div className="flex-1 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          <input data-testid="vault-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by title…" className="dms-input pl-9" />
        </div>
        <select data-testid="vault-category" value={category} onChange={(e) => setCategory(e.target.value)} className="dms-input sm:w-40">
          {CATEGORIES.map((c) => <option key={c} value={c}>{c === "all" ? "All categories" : c}</option>)}
        </select>
        <label className="flex items-center gap-2 text-[13.5px] text-[#1F2937] cursor-pointer px-2">
          <input data-testid="vault-banked-toggle" type="checkbox" checked={banked} onChange={(e) => setBanked(e.target.checked)} className="rounded" />
          <Star className="w-3.5 h-3.5" /> Banked only
        </label>
        <button type="submit" className="dms-btn-primary">Filter</button>
      </form>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {loading ? (
          <div className="col-span-full text-[#64748B] flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : items.length === 0 ? (
          <div className="col-span-full text-[#64748B] text-sm">Nothing here yet. Generate something from the dashboard.</div>
        ) : items.map((it) => (
          <div key={it.id} className="dms-card p-5 group" data-testid={`vault-item-${it.id}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] tracking-[0.18em] uppercase font-bold text-[#64748B]">{it.category}</span>
              <button onClick={() => bank(it)} title={it.is_banked ? "Unbank" : "Bank for later"} className={it.is_banked ? "text-[#F59E0B]" : "text-[#94A3B8] hover:text-[#F59E0B]"}>
                <Star className="w-4 h-4" fill={it.is_banked ? "currentColor" : "none"} />
              </button>
            </div>
            <Link to={`/vault/${it.id}`} className="block">
              <h3 className="font-heading font-bold text-[15px] text-[#0A2342] leading-snug line-clamp-2 hover:text-[#1D4E89] transition">{it.title}</h3>
              <div className="mt-2 text-[12px] text-[#94A3B8]">{new Date(it.created_at).toLocaleDateString()}</div>
            </Link>
            <div className="mt-4 flex items-center gap-1.5 text-[#64748B] text-[12px]">
              <a href={`${API}/vault/${it.id}/download/txt`} className="hover:text-[#0A2342] inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-[#F1F5F9]" title="Download TXT"><FileText className="w-3.5 h-3.5" /> TXT</a>
              <a href={`${API}/vault/${it.id}/download/docx`} className="hover:text-[#0A2342] inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-[#F1F5F9]" title="Download DOCX"><FileType className="w-3.5 h-3.5" /> DOCX</a>
              <button onClick={() => duplicate(it.id)} className="hover:text-[#0A2342] inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-[#F1F5F9]" title="Duplicate"><Copy className="w-3.5 h-3.5" /></button>
              <button onClick={() => del(it.id)} className="hover:text-[#B91C1C] inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-[#FEF2F2] ml-auto" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function VaultItem() {
  const { id } = useParams();
  const nav = useNavigate();
  const [item, setItem] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [showJson, setShowJson] = useState(false);
  const [sendingNotion, setSendingNotion] = useState(false);

  useEffect(() => {
    api.get(`/vault/${id}`).then(({ data }) => {
      setItem(data.item);
      setDraft(JSON.stringify(data.item.output, null, 2));
    }).finally(() => setLoading(false));
  }, [id]);

  const save = async () => {
    try {
      const parsed = JSON.parse(draft);
      const { data } = await api.put(`/vault/${id}`, { output: parsed });
      setItem(data.item);
      setEditing(false);
      toast.success("Saved");
    } catch { toast.error("Invalid JSON"); }
  };

  const sendToNotion = async () => {
    setSendingNotion(true);
    try {
      const { data } = await api.post(`/vault/${id}/send-to-notion`);
      toast.success("Sent to Notion", {
        action: data?.page_url
          ? { label: "Open page", onClick: () => window.open(data.page_url, "_blank") }
          : undefined,
      });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not send to Notion";
      if (err?.response?.status === 400 && /Connect Notion/i.test(msg)) {
        toast.error(msg, {
          action: { label: "Set up", onClick: () => nav("/integrations") },
        });
      } else {
        toast.error(msg);
      }
    } finally {
      setSendingNotion(false);
    }
  };

  if (loading) return <div className="p-10 text-[#64748B]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>;
  if (!item) return <div className="p-10 text-[#64748B]">Not found.</div>;

  return (
    <div data-testid="vault-item-page" className="px-5 sm:px-6 lg:px-10 py-8 sm:py-10 max-w-4xl">
      <button onClick={() => nav("/vault")} data-testid="vault-back" className="text-[13.5px] text-[#1D4E89] hover:text-[#0A2342] inline-flex items-center gap-1.5 mb-4"><ArrowLeft className="w-4 h-4" /> Back to Vault</button>
      <div className="dms-label mb-2">{item.category}</div>
      <h1 className="text-2xl sm:text-3xl font-heading font-bold text-[#0A2342]">{item.title}</h1>
      <div className="mt-2 text-[13px] text-[#64748B]">Created {new Date(item.created_at).toLocaleString()}{item.refinement_note ? ` · Refined from: "${item.refinement_note.slice(0, 80)}"` : ""}</div>

      <div className="mt-6 flex flex-wrap gap-2">
        {item.tool === "html_studio" && (
          <button onClick={() => nav(`/tools/html-studio?project=${item.id}`)} data-testid="vault-open-html-studio" className="dms-btn-primary"><Code2 className="w-4 h-4" /> Edit in HTML Studio</button>
        )}
        <a href={`${API}/vault/${item.id}/download/txt`} data-testid="download-txt" className="dms-btn-ghost"><Download className="w-4 h-4" /> TXT</a>
        <a href={`${API}/vault/${item.id}/download/docx`} data-testid="download-docx" className="dms-btn-ghost"><Download className="w-4 h-4" /> DOCX</a>
        <button onClick={async () => { await navigator.clipboard.writeText(JSON.stringify(item.output, null, 2)); toast.success("Copied"); }} data-testid="copy-json" className="dms-btn-ghost"><Copy className="w-4 h-4" /> Copy JSON</button>
        <button onClick={sendToNotion} disabled={sendingNotion} data-testid="send-to-notion" className="dms-btn-ghost">
          {sendingNotion ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending</> : <><Send className="w-4 h-4" /> Send to Notion</>}
        </button>
        <button onClick={() => setShowJson((v) => !v)} data-testid="toggle-json" className="dms-btn-ghost">
          {showJson ? <><Eye className="w-4 h-4" /> Readable view</> : <><Code2 className="w-4 h-4" /> JSON view</>}
        </button>
        {!editing ? (
          <button onClick={() => setEditing(true)} data-testid="vault-edit" className="dms-btn-primary">Edit</button>
        ) : (
          <>
            <button onClick={save} data-testid="vault-save" className="dms-btn-accent">Save</button>
            <button onClick={() => { setEditing(false); setDraft(JSON.stringify(item.output, null, 2)); }} className="dms-btn-ghost">Cancel</button>
          </>
        )}
      </div>

      <div className="mt-8">
        {editing ? (
          <div className="dms-card p-5 sm:p-6">
            <textarea value={draft} onChange={(e) => setDraft(e.target.value)} className="dms-textarea font-mono text-[13px]" rows={24} />
            <p className="mt-2 text-[11px] text-[#94A3B8]">Direct JSON edit mode. Keep valid JSON.</p>
          </div>
        ) : showJson ? (
          <div className="dms-card p-5 sm:p-6">
            <pre data-testid="vault-json-view" className="text-[12.5px] leading-relaxed text-[#1F2937] whitespace-pre-wrap font-mono overflow-x-auto">{JSON.stringify(item.output, null, 2)}</pre>
          </div>
        ) : (
          <div data-testid="vault-readable-view">
            <VaultOutputRenderer output={item.output} />
          </div>
        )}
      </div>
    </div>
  );
}

export default ContentVault;
